<template>
  <RenderComponents :schema="schema" :ComponentRegistry="ComponentRegistry" />
  <LinkPreloader />
  <DevToolsModal
    v-if="devtools.isOpen.value"
    v-model:open="devtools.isOpen.value"
    :pipe="pipe"
    @clear-home="devtools.clearStateAndReturnHome"
  />
</template>

<script setup lang="ts">
import { defineAsyncComponent } from 'vue'
import RenderComponents from '@/render/RenderComponents.vue' // adjust path if needed
import ComponentRegistry from 'virtual:component-registry'

import type { ComponentSchema, Page } from '../api/types.gen'
import {
  ERROR_CONTEXTS,
  PipeDisplayMode,
  usePipe,
  type NavigationEventPayload,
} from '@render/composables/usePipe'
import { usePipeEventHandlers } from '@render/composables/usePipeEventHandlers'
import {
  EXIT_INTENT_TRIGGER_VAR_KEY,
  pageWiresExitIntent,
  resolveExitIntentOptions,
  shouldBlockBackNavigation,
  shouldTrapBackNavigation,
  useExitIntent,
  type ExitIntentConfig,
  type ExitIntentTrigger,
} from '@render/composables/useExitIntent'
import { useAutoFocus } from '@render/composables/useAutoFocus'
import { useResetViewOnNavigation } from '@render/composables/useResetViewOnNavigation'
import { useDirection } from '@render/composables/useDirection'
import { useLocale } from '@render/composables/useLocale'
import { useDevTools } from '@render/devtools/useDevTools'
import { withGlobalSharedNodes } from '@render/lib/globalSharedNodes'

// Walks a TipTap doc-shape value (`{type:'doc', content:[{text,...}|{content:[...]}]}`)
// and concatenates all text nodes. Returns '' for non-object input.
const tiptapDocToPlainText = (node: unknown): string => {
  if (!node || typeof node !== 'object') return ''
  const obj = node as { text?: unknown; content?: unknown }
  if (typeof obj.text === 'string') return obj.text
  if (Array.isArray(obj.content)) return obj.content.map(tiptapDocToPlainText).join('')
  return ''
}

const DevToolsModal = defineAsyncComponent(() => import('@render/devtools/DevToolsModal.vue'))

// NOTE: `key: 'pipe-page'` below uses a STATIC string — Nuxt's default per-route
// key is `route.fullPath`, so setting a constant one actually causes this
// component to be REUSED across pipe pages (preserves state, avoids re-fetching).
// Because of that reuse, onMounted / onBeforeUnmount only fire once per pipe
// session — not per page transition. We drive the navigation events off a
// `route.path` watcher below instead, which works regardless of the key value.
definePageMeta({
  key: 'pipe-page',
})

const router = useRouter()
const route = useRoute()
const pipe = usePipe()
const { getEventListeners } = usePipeEventHandlers(pipe)
const devtools = useDevTools(pipe)

// Inputs autofocus
useAutoFocus()

// Reset the view to the top-left origin on every route change (scroll + a
// viewport-meta toggle that snaps a zoomed/panned view back; the reused page
// component + no router scrollBehavior otherwise carry the previous page over).
useResetViewOnNavigation()

const resolveCurrentPage = (): Page | null => {
  const page = pipe.getPageBySlug(route.path)
  if (page) return page
  const landingId = (
    pipe.data.value?.latestContent?.content as { landingPageId?: string } | undefined
  )?.landingPageId
  return landingId ? pipe.getPageById(landingId) : null
}

const schema = computed<ComponentSchema>(() => {
  const page = resolveCurrentPage()
  if (!page) return { children: [] }
  // Global shared nodes are auto-rendered on every page (first/last child per
  // their `globalPosition`); injected here so the built (and prerendered) HTML
  // includes them. SharedNodeRefRenderer resolves the synthetic ref via
  // pipe.getSharedNodeById, identical to a manually placed ref.
  const sharedNodes = pipe.data.value?.latestContent?.content.sharedNodes
  return withGlobalSharedNodes(page.content, sharedNodes, page.id)
})

// Resolve a translation-key field to plain text via the active locale.
const resolveTranslationKeyText = (
  field: { key?: string } | null | undefined,
): string | undefined => {
  const key = field?.key
  if (!key) return undefined
  const text = tiptapDocToPlainText(pipe.getTranslation(key))
  return text || undefined
}

// Pipe-wide default title, used as fallback when a page has no title set.
const defaultTitleField = computed(() => pipe.data.value?.latestContent?.content.defaultTitle)

// Resolve a page's title with fallback to the pipe-level default. Returns
// undefined only when both are unset/empty so callers can pass through to
// `useHead` (falls back to app default) or omit the field on event payloads.
const resolvePageTitle = (page: Page | null | undefined): string | undefined => {
  return (
    resolveTranslationKeyText(page?.title) ?? resolveTranslationKeyText(defaultTitleField.value)
  )
}

// Pipe-wide default meta description, used as fallback when a page has none set.
const defaultDescriptionField = computed(
  () => pipe.data.value?.latestContent?.content.defaultDescription,
)

// Resolve a page's SEO meta description with fallback to the pipe-level default.
const resolvePageDescription = (page: Page | null | undefined): string | undefined => {
  return (
    resolveTranslationKeyText(page?.description) ??
    resolveTranslationKeyText(defaultDescriptionField.value)
  )
}

// Direction + language for this release. Bound on <html> so the prerendered
// (STATIC) HTML emits `dir`/`lang` at build time — not just client-side — and
// the [dir='rtl'] CSS cascade reaches every rendered component.
const { dir } = useDirection()
const { language } = useLocale()
useHead({
  title: computed(() => resolvePageTitle(resolveCurrentPage())),
  meta: [
    {
      name: 'description',
      content: computed(() => resolvePageDescription(resolveCurrentPage())),
    },
  ],
  htmlAttrs: { dir, lang: language },
})

// Currently-shown page snapshot. Used to pair `navigation:leave` with the page
// that was actually showing, even after `route.path` has flipped to the next one.
let currentShownPage: { page: Page; path: string } | null = null

const getLandingPageId = (): string | undefined =>
  (pipe.data.value?.latestContent?.content as { landingPageId?: string } | undefined)?.landingPageId

// Reset pipe state whenever the user (re-)enters the main landing page, so
// returning users always start with a blank session — prevents a previous
// session's form answers from pre-filling for the next user on the same device.
// Mirrors the clear_all_pipe_data processor: clearPersistedData wipes localStorage
// + in-memory variables, then pipe:data-cleared triggers trackingProperties
// re-capture and any other integration reset listeners.
const clearIfLandingPage = (page: Page) => {
  if (pipe.currentMode.value === PipeDisplayMode.EDITOR) return
  if (page.id !== getLandingPageId()) return
  pipe.clearPersistedData()
  pipe.emit('pipe:data-cleared')
}

// Page-event handlers are async but invoked fire-and-forget - navigation
// shouldn't block on author-wired side effects. Attach .catch so rejections
// reach Bugsnag with context instead of escaping as unhandled.
const reportListenerRejection = (
  result: unknown,
  event: 'onPageShow' | 'onPageLeave' | 'onExitIntent',
  pageId: string,
) => {
  if (result instanceof Promise) {
    result.catch((err) =>
      pipe.trackError({
        error: err,
        context: ERROR_CONTEXTS.pageLifecycle,
        metadata: { event, pageId },
      }),
    )
  }
}

const fireShow = (page: Page, path: string) => {
  const payload: NavigationEventPayload = {
    pageId: page.id,
    path,
    title: resolvePageTitle(page),
    timestamp: Date.now(),
  }
  // Mark visited BEFORE the emit so subscribers (e.g. LinkPreloader) see the
  // current page already in the skip set when they recompute.
  pipe.markPageVisited(page.id, path)
  pipe.emit('navigation:show', payload)
  reportListenerRejection(
    getEventListeners(page.events ?? {}).onPageShow?.(payload),
    'onPageShow',
    page.id,
  )
}

const fireLeave = (page: Page, path: string) => {
  const payload: NavigationEventPayload = {
    pageId: page.id,
    path,
    title: resolvePageTitle(page),
    timestamp: Date.now(),
  }
  pipe.emit('navigation:leave', payload)
  reportListenerRejection(
    getEventListeners(page.events ?? {}).onPageLeave?.(payload),
    'onPageLeave',
    page.id,
  )
}

// Whether the page currently shown has anywhere to show an exit-intent offer:
// its own onExitIntent bucket, or one on a component or shared node in its
// tree. Also what decides whether the Back trap arms in `exitIntentPages` mode.
const pageHasExitIntentHandler = (page: Page) =>
  pageWiresExitIntent(page.events, schema.value.children, pipe.getSharedNodeById)

// Reached only through the detector's `canShow` gate below, so the page shown
// is known to wire exit intent: the variable write, the analytics emit and the
// handlers all describe an offer that is actually being made.
const fireExitIntent = (page: Page, path: string, trigger: ExitIntentTrigger) => {
  const payload: NavigationEventPayload = {
    pageId: page.id,
    path,
    title: resolvePageTitle(page),
    timestamp: Date.now(),
    trigger,
  }
  // Set before the handlers run so anything they reach (an offer switch, an
  // order upsert) already sees which mechanic brought the visitor here.
  pipe.setData(EXIT_INTENT_TRIGGER_VAR_KEY, trigger)
  pipe.emit('navigation:exit-intent', payload)
  reportListenerRejection(
    getEventListeners(page.events ?? {}).onExitIntent?.(payload),
    'onExitIntent',
    page.id,
  )
}

/**
 * Whether a trigger landing right now would lead to anything: a live page (not
 * the editor preview, and past the first render that resolves it) that wires
 * an exit-intent handler.
 *
 * Passed to the detector rather than checked inside the callback, because a
 * fire that reaches a page with nothing to show still CONSUMES the `once`
 * token — which is how a `session`-scoped pipe spent its single offer on the
 * landing page and never showed it at checkout, and how a Back on a page with
 * no offer got cancelled with nothing in its place.
 */
const canShowExitIntent = (): boolean => {
  if (!import.meta.client) return false
  if (pipe.currentMode.value === PipeDisplayMode.EDITOR) return false
  if (!currentShownPage) return false
  return pageHasExitIntentHandler(currentShownPage.page)
}

// Thresholds and opt-in mechanics come from the EXIT_INTENT integration row
// (global, brand or pipe). Read once at setup — app.vue registers the
// extensions synchronously before any page component sets up — and resolved
// against the defaults the detector used before the row existed, so a pipe
// with no row, or a row with an empty config, behaves exactly as before.
const exitIntentConfig = pipe.getIntegrationConfig<ExitIntentConfig>('EXIT_INTENT')

// Exit-intent detector. Listeners live for the lifetime of `[...slug].vue`
// (which is reused across pipe pages); `reset()` is called on each page show,
// so by default the handler fires once per page visit. The resolved cooldown
// absorbs a leave signal that lands right as a page arrives — a cursor still
// sweeping the top edge after a navigation click, or the spurious
// `visibilitychange → hidden` mobile browsers emit around a navigation gesture
// — which would otherwise fire the just-arrived page's onExitIntent.
const exitIntent = useExitIntent(
  (trigger) => {
    // `canShowExitIntent` has already established there is one; this is the
    // null check the closure needs, not a second gate.
    if (!currentShownPage) return
    fireExitIntent(currentShownPage.page, currentShownPage.path, trigger)
  },
  resolveExitIntentOptions(exitIntentConfig, {
    shouldSuppress: () => pipe.isExitIntentSuppressed(),
    canShow: canShowExitIntent,
  }),
)

// Browser Back as an exit-intent signal. Off unless the integration turns it
// on, because it changes what the browser's own control does: when the trap
// fires we cancel the navigation (vue-router restores the URL for an aborted
// pop) and the page shows its exit-intent offer instead of unwinding. A second
// Back always leaves — `request()` refuses once exit intent has been consumed,
// and so does an in-flight payment's suppressor, so the visitor is never stuck.
//
// Back is detected from the history position vue-router stamps on each entry:
// during `beforeEach` a pop has already moved `history.state` to the target,
// while a push has not written its entry yet.
if (import.meta.client) {
  const historyPosition = (): number | null => {
    const state = window.history.state as { position?: unknown } | null
    return typeof state?.position === 'number' ? state.position : null
  }

  let lastPosition = historyPosition()

  const removeBeforeEach = router.beforeEach(() => {
    if (pipe.currentMode.value === PipeDisplayMode.EDITOR) return true
    if (!currentShownPage) return true
    const position = historyPosition()
    const isBack = position !== null && lastPosition !== null && position < lastPosition
    if (!isBack) return true
    const trapped = shouldTrapBackNavigation(
      exitIntentConfig,
      pageHasExitIntentHandler(currentShownPage.page),
    )
    if (!trapped) return true
    if (!exitIntent.request('backNavigation')) return true
    return !shouldBlockBackNavigation(exitIntentConfig)
  })

  const removeAfterEach = router.afterEach(() => {
    lastPosition = historyPosition() ?? lastPosition
  })

  onBeforeUnmount(() => {
    removeBeforeEach()
    removeAfterEach()
  })
}

// Initial page show. onMounted fires AFTER all child SharedNodeRefRenderers
// have mounted and subscribed, so they receive this live (no history replay
// needed, which is good because subscribe replays ALL past matching events).
//
// onPageShow fires in EDITOR mode too — this is intentional so authors can
// preview state-driven components like ProgressBarPOC, whose setProgress
// processor is wired to page.onPageShow and needs to run in the preview
// iframe to show the progress value for the page being edited. Leave / exit
// / unmount events stay editor-gated below so cleanup side-effects don't
// surprise authors.
onMounted(() => {
  if (!import.meta.client) return
  const page = resolveCurrentPage()
  if (page) {
    clearIfLandingPage(page)
    currentShownPage = { page, path: route.path }
    fireShow(page, route.path)
    exitIntent.reset()
    return
  }
  // Late-loaded pipe data: in the editor (and on a hard cmd+R refresh of
  // the editor), the iframe's [...slug].vue can mount before the parent
  // app has finished loading the pipe — `resolveCurrentPage()` returns
  // null and the initial fireShow above is skipped. Watch once for the
  // page to become available, then await nextTick so child components
  // (SharedNodeRefRenderers and `useComponentProcessors` registrations,
  // e.g. ProgressBarPOC's setProgress) have finished mounting before we
  // dispatch the show — otherwise the processor wouldn't be registered
  // when the page-show event fires and the bar would stay at 0.
  const stop = watch(
    () => resolveCurrentPage(),
    async (latePage) => {
      if (!latePage) return
      stop()
      await nextTick()
      clearIfLandingPage(latePage)
      currentShownPage = { page: latePage, path: route.path }
      fireShow(latePage, route.path)
      exitIntent.reset()
    },
    { flush: 'post' },
  )
})

// Page-to-page transitions. The page component is reused (see note on
// `key: 'pipe-page'` above), so lifecycle hooks alone don't see navigation —
// we watch route.path instead.
//
// Ordering matters:
//   1. Emit `navigation:leave` BEFORE Vue re-renders, so any
//      SharedNodeRefRenderer rendered only on the leaving page is still
//      mounted and receives it.
//   2. await nextTick() to let Vue render the new page schema — this mounts
//      any SharedNodeRefRenderers introduced by the new page and gives them
//      a chance to subscribe.
//   3. Emit `navigation:show` — now the new subscribers are in place.
watch(
  () => route.path,
  async (newPath, oldPath) => {
    if (!import.meta.client) return
    if (!oldPath || oldPath === newPath) return
    const isEditor = pipe.currentMode.value === PipeDisplayMode.EDITOR

    // Leave is editor-gated to avoid running cleanup-style author-wired logic
    // when the author is just navigating between pages in the preview iframe.
    if (currentShownPage && !isEditor) {
      fireLeave(currentShownPage.page, currentShownPage.path)
      currentShownPage = null
    }

    await nextTick()

    const newPage = resolveCurrentPage()
    if (!newPage) return
    clearIfLandingPage(newPage)
    currentShownPage = { page: newPage, path: newPath }
    // Show fires in editor too so authors can preview the new page's
    // state-driven components — the ProgressBarPOC's setProgress processor
    // wired to page.onPageShow needs this to render the page's progress
    // when navigating between pages in the editor preview.
    fireShow(newPage, newPath)
    exitIntent.reset()
  },
)

// Fires only when the user navigates out of `[...slug].vue` entirely (e.g. to
// a non-pipe route). Page-to-page transitions within the pipe don't unmount.
onBeforeUnmount(() => {
  if (!import.meta.client) return
  if (pipe.currentMode.value === PipeDisplayMode.EDITOR) return
  if (!currentShownPage) return
  fireLeave(currentShownPage.page, currentShownPage.path)
  currentShownPage = null
})
</script>
